/**
 * Sync Hook
 *
 * Initializes WebSocket connection and syncs state between frontend and backend.
 * Should be called once at the app root level.
 */

import { useEffect, useRef } from 'react';
import { useSessionStore, useConnectionStore } from '@/stores';
import { wsService } from '@/services/websocket';

interface UseSyncOptions {
  autoConnect?: boolean;
}

/**
 * Hook to manage WebSocket synchronization
 */
export function useSync(options: UseSyncOptions = {}) {
  const { autoConnect = true } = options;

  const sessionId = useSessionStore((state) => state.sessionId);
  const isInitialized = useSessionStore((state) => state.isInitialized);
  const createSession = useSessionStore((state) => state.createSession);
  const connectionStatus = useConnectionStore((state) => state.status);

  const hasInitialized = useRef(false);

  // Initialize session on mount
  useEffect(() => {
    if (hasInitialized.current || !autoConnect) return;
    hasInitialized.current = true;

    createSession().catch((error) => {
      console.warn('Failed to create session:', error);
    });
  }, [autoConnect, createSession]);

  // Connect WebSocket when session is ready
  useEffect(() => {
    if (!sessionId || !isInitialized) return;

    // Only connect if we have Redis support (session endpoints work)
    wsService.connect(sessionId);

    return () => {
      wsService.disconnect();
    };
  }, [sessionId, isInitialized]);

  return {
    sessionId,
    isInitialized,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
  };
}

/**
 * Hook to check if sync is available
 */
export function useSyncStatus() {
  const connectionStatus = useConnectionStore((state) => state.status);
  const latency = useConnectionStore((state) => state.latency);
  const error = useConnectionStore((state) => state.error);

  return {
    status: connectionStatus,
    isConnected: connectionStatus === 'connected',
    isReconnecting: connectionStatus === 'reconnecting',
    latency,
    error,
  };
}
